/**
 * DD-SALESWAIT-005R — @Sales → threaded-reply wait intervals on a workflow line.
 *
 * Canon (Taylor): a mention opens a wait; it clears only when someone Replies
 * to that message (direct child). Plain new roots / wrong-thread posts do not
 * clear. Source / on- prefix are NOT used for clear (provenance only).
 *
 * Mention-in-reply fallback: UI cannot reply-to a reply; a later sibling under
 * the same root clears that mention. Root mentions require a direct child (A1).
 *
 * totalAwaitingSalesMs: merge overlapping/touching intervals before summing
 * (snapshot-on-render uses Date.now() for open ends).
 */
import type { ChatMessage } from '@/lib/dd-queue/types'

export interface SalesWaitStats {
  /** True if any @Sales mention still lacks a clearing reply. */
  awaitingSalesReply: boolean
  /** Unix-ms of the earliest open wait (null when not waiting). */
  awaitingSalesSince: number | null
  /**
   * Total ms spent waiting for Sales across merged @Sales → reply intervals
   * on this line (open ends use Date.now() at computation time).
   */
  totalAwaitingSalesMs: number
}

/** One @Sales → reply wait cycle. `to` is null while the wait is still open. */
export interface SalesWaitInterval {
  from: number
  to: number | null
}

function mentionsSales(msg: ChatMessage): boolean {
  return msg.mentions?.includes('Sales') ?? false
}

/** HS-NOTES-C1-001 / A1 — inbound HubSpot notes never open or clear SalesWait. */
function isHubSpotSource(msg: ChatMessage): boolean {
  return msg.source === 'hubspot'
}

function byTsAsc(a: ChatMessage, b: ChatMessage): number {
  return (a.ts ?? 0) - (b.ts ?? 0)
}

/**
 * First clearing reply for mention M.
 * - Prefer direct children in chatReplies[M.id] with ts >= M.ts.
 * - If M is a reply under rootId: later sibling under that root (ts > M.ts).
 */
function firstClearTs(
  mention: ChatMessage,
  rootId: string | null,
  chatReplies: Record<string, ChatMessage[]>
): number | null {
  const direct = [...(chatReplies[mention.id] ?? [])]
    .filter(
      (r) =>
        !isHubSpotSource(r) &&
        r.ts != null &&
        (r.ts as number) >= (mention.ts as number)
    )
    .sort(byTsAsc)
  if (direct[0]?.ts != null) {
    return direct[0].ts
  }

  // Mention-in-reply sibling fallback (UI cannot target replies as parents).
  if (rootId != null && mention.id !== rootId) {
    const siblings = [...(chatReplies[rootId] ?? [])]
      .filter(
        (r) =>
          !isHubSpotSource(r) &&
          r.id !== mention.id &&
          r.ts != null &&
          (r.ts as number) > (mention.ts as number)
      )
      .sort(byTsAsc)
    if (siblings[0]?.ts != null) {
      return siblings[0].ts
    }
  }

  return null
}

/**
 * The @Sales → reply wait cycles on a line, oldest first.
 * Consumed by dwell excludeIntervals (DD-DWELL-PAUSE-001). Clear = threaded Reply (005R).
 */
export function salesWaitIntervals(
  chat: ChatMessage[],
  chatReplies: Record<string, ChatMessage[]>
): SalesWaitInterval[] {
  const intervals: SalesWaitInterval[] = []

  for (const root of chat) {
    if (isHubSpotSource(root)) continue
    if (root.ts != null && mentionsSales(root)) {
      intervals.push({
        from: root.ts,
        to: firstClearTs(root, null, chatReplies),
      })
    }

    for (const reply of chatReplies[root.id] ?? []) {
      if (isHubSpotSource(reply)) continue
      if (reply.ts != null && mentionsSales(reply)) {
        intervals.push({
          from: reply.ts,
          to: firstClearTs(reply, root.id, chatReplies),
        })
      }
    }
  }

  return intervals.sort((a, b) => a.from - b.from)
}

/** Merge overlapping/touching half-open ranges [from, to] (to may be null → now). */
export function mergeSalesWaitIntervals(
  intervals: SalesWaitInterval[],
  now: number
): { from: number; to: number }[] {
  const closed = intervals
    .map((iv) => ({ from: iv.from, to: iv.to ?? now }))
    .filter((iv) => iv.to >= iv.from)
    .sort((a, b) => a.from - b.from)

  if (closed.length === 0) return []

  const merged: { from: number; to: number }[] = [{ ...closed[0] }]
  for (let i = 1; i < closed.length; i++) {
    const cur = closed[i]
    const last = merged[merged.length - 1]
    if (cur.from <= last.to) {
      last.to = Math.max(last.to, cur.to)
    } else {
      merged.push({ ...cur })
    }
  }
  return merged
}

export function salesWaitStats(
  chat: ChatMessage[],
  chatReplies: Record<string, ChatMessage[]>
): SalesWaitStats {
  const intervals = salesWaitIntervals(chat, chatReplies)
  const open = intervals.filter((iv) => iv.to === null)
  const now = Date.now()
  const merged = mergeSalesWaitIntervals(intervals, now)
  const totalMs = merged.reduce((sum, iv) => sum + (iv.to - iv.from), 0)
  const earliestOpen =
    open.length === 0
      ? null
      : Math.min(...open.map((iv) => iv.from))

  return {
    awaitingSalesReply: open.length > 0,
    awaitingSalesSince: earliestOpen,
    totalAwaitingSalesMs: totalMs,
  }
}
